// RFC-0944 — load/save orchestration for the PROJECT-scoped rules modal.
//
// Mirrors useUserRules (same monotonic stale-guard + best-effort history
// refresh), with two project-specific differences:
//   - Reads the PROJECT fields of the shared RulesResponse (`project`,
//     `project_current_version`) instead of the user ones.
//   - Derives `canWrite` from the owner-gated `history` field. Because the hook
//     always requests `history=true`, the backend's owner gating makes
//     `resp.history` an unambiguous ownership signal: an OWNER with no project
//     rules yet gets `project=null, version=0, history=[]` (non-null empty
//     array), while a NON-OWNER gets `history=null`. So `canWrite =
//     resp.history !== null` distinguishes the two `project===null` cases
//     WITHOUT a backend change; it drives `isReadOnly` in the editor.

import { useCallback, useEffect, useRef, useState } from 'react'

import {
  getProjectRules,
  putProjectRules,
  type RulesDocumentV1,
  type RulesHistoryEntry,
  type RulesPutBody,
} from '../api/rules'

export interface UseProjectRulesResult {
  doc: RulesDocumentV1 | null
  version: number
  history: RulesHistoryEntry[]
  /** True when the requester owns the project (owner-gated `history !== null`). */
  canWrite: boolean
  loading: boolean
  loadError: string | null
  reload: () => void
  save(
    body: RulesPutBody,
  ): Promise<{ new_version: number; document: RulesDocumentV1 } | void>
}

export function useProjectRules(
  projectId: string,
  isOpen: boolean,
): UseProjectRulesResult {
  const [doc, setDoc] = useState<RulesDocumentV1 | null>(null)
  const [version, setVersion] = useState<number>(0)
  const [history, setHistory] = useState<RulesHistoryEntry[]>([])
  const [canWrite, setCanWrite] = useState<boolean>(false)
  const [loading, setLoading] = useState<boolean>(false)
  const [loadError, setLoadError] = useState<string | null>(null)

  // Monotonic sequence: only the most recent load may commit its result. A
  // late-arriving response from a superseded request is dropped.
  const loadSeqRef = useRef(0)
  // Separate monotonic sequence gating EVERY `history` write (full loads AND
  // standalone history refreshes) so two quick saves can't let an older
  // refresh overwrite a newer revert list.
  const historySeqRef = useRef(0)
  const wasOpenRef = useRef(false)

  const load = useCallback(() => {
    const seq = ++loadSeqRef.current
    const hseq = ++historySeqRef.current
    setLoading(true)
    setLoadError(null)
    getProjectRules(projectId, { history: true })
      .then((resp) => {
        if (seq !== loadSeqRef.current) return
        setDoc(resp.project ?? null)
        setVersion(resp.project_current_version ?? 0)
        // Owner-gated: history is a non-null array for owners (incl. the
        // no-rules-yet empty case) and null for non-owners. Drives isReadOnly.
        setCanWrite(resp.history !== null)
        if (hseq === historySeqRef.current) setHistory(resp.history ?? [])
        setLoading(false)
      })
      .catch((err) => {
        if (seq !== loadSeqRef.current) return
        setLoadError(err instanceof Error ? err.message : String(err))
        setLoading(false)
      })
  }, [projectId])

  // Fetch on every closed→open transition; never cache-forever (project rules
  // can change via the Director or another tab).
  useEffect(() => {
    if (isOpen && !wasOpenRef.current) {
      load()
    }
    wasOpenRef.current = isOpen
  }, [isOpen, load])

  // Best-effort refresh of just the history list after a successful write. The
  // PUT response carries the new doc/version but not history, and a stale
  // revert list would offer reverts that no longer make sense. Gated by
  // `historySeqRef` (bumped per refresh AND per load) so a slower refresh —
  // e.g. from an earlier of two quick saves — can't overwrite a newer list.
  const refreshHistory = useCallback(() => {
    const hseq = ++historySeqRef.current
    getProjectRules(projectId, { history: true })
      .then((resp) => {
        if (hseq !== historySeqRef.current) return
        setHistory(resp.history ?? [])
      })
      .catch(() => {
        /* the write already succeeded; a failed history refresh is non-fatal */
      })
  }, [projectId])

  const save = useCallback(
    async (
      body: RulesPutBody,
    ): Promise<{ new_version: number; document: RulesDocumentV1 } | void> => {
      const resp = await putProjectRules(projectId, body)
      // A write always returns the persisted project document; a null here is
      // an invariant violation (unlike load()'s GET, where null = "no rules
      // yet" or "non-owner"). Fail loudly rather than silently resetting the
      // editor to empty.
      if (resp.project === null) {
        throw new Error(
          'putProjectRules returned a null project document after a write',
        )
      }
      // Derive/return the PUT result FIRST so the editor shows "saved"; only
      // then kick off the non-fatal background history refresh.
      setDoc(resp.project)
      setVersion(resp.project_current_version)
      refreshHistory()
      return { new_version: resp.project_current_version, document: resp.project }
    },
    [projectId, refreshHistory],
  )

  return { doc, version, history, canWrite, loading, loadError, reload: load, save }
}
